import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation, useNavigate } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext"; // Import AuthProvider
import { SidebarProvider } from "./contexts/SidebarContext";
import Home from "./pages/Home";
import Profile from "./pages/Profile";
import JobSearch from "./pages/JobSearch";
import CareerAdvice from "./pages/CareerAdvice";
import ExploreCompanies from "./pages/ExploreCompanies";
import JobPostingForm from "./pages/JobPostingForm";
import ArticleDetails from "./pages/ArticleDetails";
import JobDetails from "./pages/JobDetails";
import AddCompany from "./pages/AddCompany";
import CompanyDetails from "./pages/CompanyDetails";
import Login from "./pages/Login";
import CreateAccount from "./pages/CreateAccount";
import VerifyEmail from "./pages/VerifyEmail";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import AddCareerAdvice from "./pages/AddCareerAdvice";
import CandidatesPage from './pages/CandidatesPage';
import EditProfile from "./pages/EditProfile";
import RecruiterDashboard from "./pages/RecruiterDashboard";
import EditJobPost from "./pages/EditJobPost";
import JobCandidates from './pages/JobCandidates';
import CandidateDetails from './pages/CandidateDetails';
import AssessmentPage from './pages/AssessmentPage';
import MyApplications from './pages/MyApplications';
import GlobalSidebar from "./components/GlobalSidebar";
import Onboarding from "./pages/Onboarding";
import RecruiterOnbp from "./pages/RecruiterOnbp";
import CommonOnboard from "./pages/CommonOnboard";
import { useAuth } from "./contexts/AuthContext";
import { API_BASE_URL } from "./config/apiconfig";
import AdminDashboardLayout from "./pages/AdminDashboardLayout";
import AdminProductsMachines from "./pages/AdminProductsMachines";
import AdminDepartmentsDesignations from "./pages/AdminDepartmentsDesignations";
import AdminCompanies from "./pages/AdminCompanies";
import CompanyAdminDashboard from "./pages/CompanyAdminDashboard";
import ClusterAdminDashboard from "./pages/ClusterAdminDashboard";
import UnitAdminDashboard from "./pages/UnitAdminDashboard";
import WaitingApproval from "./pages/WaitingApproval";
import InactiveAccount from "./pages/InactiveAccount";

function RequireOnboarding({ children }) {
    const { user } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [checking, setChecking] = useState(true);
    const [needsOnboarding, setNeedsOnboarding] = useState(false);

    useEffect(() => {
        let cancelled = false;
        async function check() {
            // Reset checking at the start of each run to block redirects until fresh status is known
            if (!cancelled) setChecking(true);
            if (!user || location.pathname === "/onboarding") {
                if (!cancelled) setChecking(false);
                return;
            }
            // Skip onboarding enforcement for admin roles (early exit to avoid choose-role/onboarding redirects)
            if (user.role === 'company-admin' || user.role === 'cluster-admin' || user.role === 'unit-admin' || user.role === 'e2e-admin') {
                if (!cancelled) setChecking(false);
                return;
            }
            // If user has no role or onboarding flags indicate not started, send to common onboarding page
            if (location.pathname !== "/choose-role") {
                const noRole = !user.role;
                const flagsUnset = (Number(user.onboarding_step || 0) === 0) && (Number(user.onboarding_completed || 0) === 0);
                if (noRole || flagsUnset) {
                    if (!cancelled) setChecking(false);
                    return navigate('/choose-role', { replace: true });
                }
            }
            // If coming from onboarding completion, bypass once and strip the query to avoid ping-pong
            const params = new URLSearchParams(location.search);
            if (params.get('onboarded') === '1') {
                if (!cancelled) {
                    setNeedsOnboarding(false);
                    setChecking(false);
                }
                // Replace current URL to remove the onboarded flag so it doesn't persist
                navigate(location.pathname, { replace: true });
                return;
            }
            // Skip onboarding enforcement for company/cluster/unit admins (redundant safeguard)
            if (user.role === 'company-admin' || user.role === 'cluster-admin' || user.role === 'unit-admin' || user.role === 'e2e-admin') {
                if (!cancelled) setChecking(false);
                return;
            }
            // Recruiter onboarding enforcement
            if (user.role === 'recruiter') {
                try {
                    if (location.pathname === "/recruiter-onboarding") {
                        if (!cancelled) setChecking(false);
                        return;
                    }
                    const res = await fetch(`${API_BASE_URL}/api/recruiter/profile/${user.id}`, { cache: 'no-store' });
                    if (!res.ok) {
                        if (!cancelled) {
                            setNeedsOnboarding(true);
                            setChecking(false);
                        }
                        return;
                    }
                    const data = await res.json();
                    // Determine if onboarding is complete
                    const complete = (typeof data.onboarding_completed !== 'undefined')
                        ? !!data.onboarding_completed
                        : !!(data.first_name && data.last_name && data.work_email && data.phone && data.company_id);
                    // If onboarding is not complete, allow access to recruiter onboarding and skip approval/inactive checks for now
                    if (!complete) {
                        if (!cancelled) {
                            setNeedsOnboarding(true);
                            setChecking(false);
                        }
                        return;
                    }
                    // If approved and active enforcement only after completion
                    if (data?.approval_status !== 'approved') {
                        if (!cancelled) setChecking(false);
                        return navigate('/waiting-approval', { replace: true });
                    }
                    if (data?.account_status !== 'active') {
                        if (!cancelled) setChecking(false);
                        return navigate('/inactive-account', { replace: true });
                    }
                    if (!cancelled) {
                        setNeedsOnboarding(false);
                        setChecking(false);
                    }
                    return;
                } catch (e) {
                    if (!cancelled) {
                        setNeedsOnboarding(true);
                        setChecking(false);
                    }
                    return;
                }
            }
            try {
                const res = await fetch(`${API_BASE_URL}/profile/${user.email}`, { cache: 'no-store' });
                if (!res.ok) {
                    // No profile yet -> needs onboarding
                    if (!cancelled) setNeedsOnboarding(true);
                } else {
                    const data = await res.json();
                    const isCompleted = (Number(data.onboarding_completed) === 1) || (Number(data.onboarding_step) >= 4);
                    if (!isCompleted) {
                        try {
                            const k = user?.email ? `onboarded:${user.email}` : null;
                            if (k) localStorage.removeItem(k);
                        } catch {}
                    }
                    if (!cancelled) setNeedsOnboarding(!isCompleted);
                }
            } catch (e) {
                if (!cancelled) setNeedsOnboarding(true);
            } finally {
                if (!cancelled) setChecking(false);
            }
        }
        check();
        return () => { cancelled = true; };
    }, [user, location.pathname, location.search, navigate]);

    // If not authenticated, redirect to login immediately (prevents empty protected pages via back button)
    if (!user) {
        return <Navigate to="/login" replace state={{ from: location }} />;
    }

    // While checking, avoid rendering children to prevent flicker/loops
    if (checking) return <div className="min-h-screen flex items-center justify-center text-gray-500">Loading...</div>;
    if (needsOnboarding) {
        const allowDuringOnboarding = new Set(['/choose-role', '/onboarding', '/recruiter-onboarding']);
        if (!allowDuringOnboarding.has(location.pathname)) {
            const to = (user?.role === 'recruiter') ? '/recruiter-onboarding' : '/onboarding';
            return <Navigate to={to} replace state={{ from: location }} />;
        }
    }
    return children;
}

function PublicOnly({ children }) {
    const { user } = useAuth();
    const location = useLocation();
    if (user) {
        let to = '/home';
        if (user.role === 'e2e-admin') to = '/admin-dashboard';
        else if (user.role === 'recruiter') to = '/recruiter-dashboard';
        else if (user.role === 'company-admin') to = '/company-admin';
        else if (user.role === 'cluster-admin') to = '/cluster-admin';
        else if (user.role === 'unit-admin') to = '/unit-admin';
        return <Navigate to={to} replace state={{ from: location }} />;
    }
    return children;
}

function NeedsOnboardingOnly({ children }) {
    const { user } = useAuth();
    const location = useLocation();
    const [checking, setChecking] = useState(true);
    const [allowed, setAllowed] = useState(false);

    useEffect(() => {
        let cancelled = false;
        async function check() {
            if (!user) {
                if (!cancelled) {
                    setAllowed(false);
                    setChecking(false);
                }
                return;
            }
            // Admin roles should never see onboarding pages
            if (user.role === 'company-admin' || user.role === 'cluster-admin' || user.role === 'unit-admin' || user.role === 'e2e-admin') {
                if (!cancelled) {
                    setAllowed(false);
                    setChecking(false);
                }
                return;
            }
            try {
                if (user.role === 'recruiter') {
                    const res = await fetch(`${API_BASE_URL}/api/recruiter/profile/${user.id}`, { cache: 'no-store' });
                    if (!res.ok) throw new Error('no recruiter profile');
                    const data = await res.json();
                    const complete = !!data.onboarding_completed || !!(data.first_name && data.last_name && data.work_email && data.phone && data.company_id);
                    if (!cancelled) {
                        setAllowed(!complete);
                        setChecking(false);
                    }
                } else {
                    const res = await fetch(`${API_BASE_URL}/profile/${user.email}`, { cache: 'no-store' });
                    if (!res.ok) {
                        if (!cancelled) {
                            setAllowed(true);
                            setChecking(false);
                        }
                        return;
                    }
                    const data = await res.json();
                    const complete = !!data.onboarding_completed || (typeof data.onboarding_step === 'number' && data.onboarding_step >= 4);
                    if (!cancelled) {
                        setAllowed(!complete);
                        setChecking(false);
                    }
                }
            } catch {
                if (!cancelled) {
                    setAllowed(true);
                    setChecking(false);
                }
            }
        }
        check();
        return () => { cancelled = true; };
    }, [user, location.pathname]);

    if (checking) return <div className="min-h-screen flex items-center justify-center text-gray-500">Loading...</div>;
    if (!allowed) {
        // Set a short-lived grace flag to prevent immediate onboarding redirects
        try {
            const key = user?.email ? `onboarded:${user.email}` : null;
            if (key) sessionStorage.setItem(key, String(Date.now()));
        } catch {}
        let to = '/profile';
        if (user?.role === 'e2e-admin') to = '/admin-dashboard';
        else if (user?.role === 'recruiter') to = '/recruiter-dashboard';
        else if (user?.role === 'company-admin') to = '/company-admin';
        else if (user?.role === 'cluster-admin') to = '/cluster-admin';
        else if (user?.role === 'unit-admin') to = '/unit-admin';
        return <Navigate to={to} replace state={{ from: location, noOnboardedParam: true }} />;
    }
    return children;
    }
// Home route that is public for visitors but gated by onboarding when authenticated
function HomeGate() {
    const { user } = useAuth();
    return user ? (
        <RequireOnboarding>
            <Home />
        </RequireOnboarding>
    ) : (
        <Home />
    );
}

function App() {
    return (
        <AuthProvider> {/* Wrap your app with AuthProvider */}
            <SidebarProvider>
                <div className="min-h-screen bg-gray-50 text-gray-800">
                    <Router>
                        {/* Global slide-in Sidebar mounted once */}
                        <GlobalSidebar />
                        <div className="w-full">
                        <Routes>
                        <Route path="/choose-role" element={<CommonOnboard />} />
                        <Route path="/onboarding" element={<NeedsOnboardingOnly><Onboarding /></NeedsOnboardingOnly>} />
                        <Route path="/recruiter-onboarding" element={<NeedsOnboardingOnly><RecruiterOnbp /></NeedsOnboardingOnly>} />
                        <Route path="/" element={<Navigate to="/home" replace />} />
                        <Route path="/home" element={<HomeGate />} />
                        <Route path="/admin-dashboard/*" element={<RequireOnboarding><AdminDashboardLayout /></RequireOnboarding>} >
                            <Route index element={<AdminProductsMachines />} />
                            <Route path="products-machines" element={<AdminProductsMachines />} />
                            <Route path="departments-designations" element={<AdminDepartmentsDesignations />} />
                            <Route path="companies" element={<AdminCompanies />} />
                        </Route>
                        <Route path="/profile" element={<RequireOnboarding><Profile /></RequireOnboarding>} />
                        <Route path="/job-search" element={<RequireOnboarding><JobSearch /></RequireOnboarding>} />
                        <Route path="/career-advice" element={<RequireOnboarding><CareerAdvice /></RequireOnboarding>} />
                        <Route path="/explore-companies" element={<RequireOnboarding><ExploreCompanies /></RequireOnboarding>} />
                        <Route path="/employer-site" element={<RequireOnboarding><RecruiterDashboard /></RequireOnboarding>} />
                        <Route path="/create-job" element={<RequireOnboarding><JobPostingForm /></RequireOnboarding>} />
                        <Route path="/articles/:id" element={<RequireOnboarding><ArticleDetails /></RequireOnboarding>} />
                        <Route path="/job-details/:id" element={<RequireOnboarding><JobDetails /></RequireOnboarding>} />
                        <Route path="/add-company" element={<RequireOnboarding><AddCompany /></RequireOnboarding>} />
                        <Route path="/companies/:id" element={<RequireOnboarding><CompanyDetails /></RequireOnboarding>} />
                        <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
                        <Route path="/forgot-password" element={<PublicOnly><ForgotPassword /></PublicOnly>} />
                        <Route path="/reset-password" element={<PublicOnly><ResetPassword /></PublicOnly>} />
                        <Route path="/waiting-approval" element={<WaitingApproval />} />
                        <Route path="/company-admin" element={<CompanyAdminDashboard />} />
                        <Route path="/cluster-admin" element={<ClusterAdminDashboard />} />
                        <Route path="/unit-admin" element={<UnitAdminDashboard />} />
                        <Route path="/inactive-account" element={<InactiveAccount />} />
                        <Route path="/create-account" element={<PublicOnly><CreateAccount /></PublicOnly>} />
                        <Route path="/verify-email" element={<PublicOnly><VerifyEmail /></PublicOnly>} />
                        <Route path="/add-career-advice" element={<RequireOnboarding><AddCareerAdvice /></RequireOnboarding>} />
                        <Route path="/candidates" element={<RequireOnboarding><CandidatesPage /></RequireOnboarding>} />
                        <Route path="/profile/edit" element={<RequireOnboarding><EditProfile /></RequireOnboarding>} />
                        <Route path="/recruiter-dashboard" element={<RequireOnboarding><RecruiterDashboard /></RequireOnboarding>} />
                        <Route path="/edit-job/:id" element={<RequireOnboarding><EditJobPost /></RequireOnboarding>} />
                        <Route path="/job-candidates/:id" element={<RequireOnboarding><JobCandidates /></RequireOnboarding>} />
                        <Route path="/recruiter/job-candidates/:id" element={<RequireOnboarding><JobCandidates /></RequireOnboarding>} />
                        <Route path="/candidate-details/:email" element={<RequireOnboarding><CandidateDetails /></RequireOnboarding>} />
                        <Route path="/assessment/:applicationId" element={<RequireOnboarding><AssessmentPage /></RequireOnboarding>} />
                        <Route path="/my-applications" element={<RequireOnboarding><MyApplications /></RequireOnboarding>} />
                        </Routes>
                        </div>
                    </Router>
                </div>
            </SidebarProvider>
        </AuthProvider>
    );
}

export default App;